/**
 * Cameroon Land Market - Enterprise Platform Security & Anti-Fraud Service
 * 
 * Provides:
 * 1. Client-Side Input Sanitization (Anti-XSS & Payload Stripping)
 * 2. In-Memory & Session Storage Rate Limiting (Anti-Bot / Anti-Spam Flood)
 * 3. Cadastral Anti-Fraud & Scam Keyword Detection (MINDCAF / MoMo Fraud Alert)
 * 4. Telephone Privacy Obfuscation (Anti-Scraping Shield)
 */

/**
 * Escapes unsafe HTML characters to prevent Cross-Site Scripting (XSS).
 */
export function sanitizeText(input?: string | null): string {
  if (!input) return '';
  return input
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;')
    .trim();
}

import { sanitizeAntiInjection, sanitizeSearchTerm, isSqlOrNoSqlInjection } from './sanitize';

export { sanitizeAntiInjection, sanitizeSearchTerm, isSqlOrNoSqlInjection };

/**
 * Strips script tags, event handlers, SQL/NoSQL injection signatures, and dangerous payloads from user strings.
 */
export function sanitizeSafeString(input?: string | null, maxLength = 5000): string {
  if (!input) return '';
  return sanitizeAntiInjection(input, maxLength);
}

/**
 * Obfuscates phone digits against automated scrapers while keeping it readable for genuine visitors.
 */
export function obfuscatePhone(phone?: string | null): string {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '');
  if (digits.length < 8) return phone;
  // E.g. +237 6•• •• •• 88
  return phone.replace(/(\d{2})(\d{4,5})(\d{2})$/, '$1 •• •• $3');
}

/**
 * Client-Side Rate Limiter:
 * Prevents automated scripts and bots from flooding contact forms, inquiries, or auth challenges.
 */
interface RateLimitRecord {
  count: number;
  resetAt: number;
}

const memoryRateLimitStore: Record<string, RateLimitRecord> = {};

export function checkRateLimit(
  actionKey: string,
  maxRequests = 5,
  windowSeconds = 60
): { allowed: boolean; waitSeconds?: number } {
  const now = Date.now();
  const windowMs = windowSeconds * 1000;
  
  let record = memoryRateLimitStore[actionKey];

  if (!record || now > record.resetAt) {
    memoryRateLimitStore[actionKey] = {
      count: 1,
      resetAt: now + windowMs,
    };
    return { allowed: true };
  }

  if (record.count >= maxRequests) {
    const waitSeconds = Math.ceil((record.resetAt - now) / 1000);
    return { allowed: false, waitSeconds };
  }

  record.count += 1;
  return { allowed: true };
}

/**
 * Known Real Estate Scam Indicators in Cameroon:
 * Detects suspicious solicitation patterns such as advance Mobile Money requests before visits,
 * fake western union transfers, or unauthorized overseas wire instructions.
 */
const HIGH_RISK_SCAM_PATTERNS = [
  /payer.*d['’]avance.*(orange|momo|mtn)/i,
  /avance.*avant.*visite/i,
  /payer.*avant.*visiter/i,
  /frais.*de.*carburant.*avant/i,
  /frais.*de.*dossier.*avant/i,
  /western\s*union/i,
  /money\s*gram/i,
  /cryptocurrency|bitcoin|usdt|binance/i,
  /envoyer.*acompte.*urgent/i,
  /transfer.*before.*inspection/i,
  /pay.*deposit.*momo/i,
  /fuel.*money.*before/i,
];

const PHISHING_LINK_PATTERNS = [
  /(https?:\/\/)?(bit\.ly|tinyurl\.com|t\.co|goo\.gl|cutt\.ly|ow\.ly|is\.gd|v\.gd)\/[a-zA-Z0-9_-]+/i,
  /\b[a-zA-Z0-9-]+\.(xyz|top|ru|tk|ml|ga|cf|gq|work|buzz|club|online)\b/i,
  /(telegram\.me|t\.me\/)[a-zA-Z0-9_]+/i,
];

export interface ScamCheckResult {
  isSuspicious: boolean;
  warningNotice?: string;
}

export function evaluateContentSecurity(text?: string | null, isFr = true): ScamCheckResult {
  if (!text) return { isSuspicious: false };

  for (const pattern of HIGH_RISK_SCAM_PATTERNS) {
    if (pattern.test(text)) {
      return {
        isSuspicious: true,
        warningNotice: isFr
          ? 'Attention : Ce contenu mentionne des paiements préalables ou transferts financiers d’urgence. Ne versez JAMAIS d’argent avant la visite physique et l’inspection notariale du Titre Foncier.'
          : 'Warning: This listing mentions advance fee requests or urgent financial transfers. NEVER transfer funds via Mobile Money before physically inspecting the land and verifying the Titre Foncier with a licensed Notary.',
      };
    }
  }

  return { isSuspicious: false };
}

export interface InquiryRiskEvaluation {
  isSuspicious: boolean;
  riskScore: 'low' | 'medium' | 'high';
  warningNotice?: string;
  flaggedReasons: string[];
}

/**
 * Enterprise Risk & Anti-Fraud Evaluator for Buyer/Seller Messages:
 * Detects advance fee fraud, deceptive Mobile Money deposit demands, and phishing links.
 */
export function evaluateInquiryRisk(message?: string | null, isFr = true): InquiryRiskEvaluation {
  if (!message) return { isSuspicious: false, riskScore: 'low', flaggedReasons: [] };

  const flagged: string[] = [];

  for (const pattern of HIGH_RISK_SCAM_PATTERNS) {
    if (pattern.test(message)) {
      flagged.push('advance_payment_or_urgent_money_transfer');
    }
  }

  for (const pattern of PHISHING_LINK_PATTERNS) {
    if (pattern.test(message)) {
      flagged.push('unverified_external_or_shortened_link');
    }
  }

  if (flagged.length > 0) {
    return {
      isSuspicious: true,
      riskScore: 'high',
      flaggedReasons: flagged,
      warningNotice: isFr
        ? '⚠️ Attention Sécurité : Ce message contient des sollicitations financières suspectes (acompte avant visite ou liens externes non vérifiés). Ne versez JAMAIS d\'argent par Mobile Money avant la visite physique et l\'authentification du Titre Foncier chez un notaire assermenté.'
        : '⚠️ Security Alert: This message contains suspicious financial solicitations (advance fee demands or unverified external links). NEVER send funds via Mobile Money before physically inspecting the land and verifying the Land Title Deed with a licensed Notary.',
    };
  }

  return { isSuspicious: false, riskScore: 'low', flaggedReasons: [] };
}

/**
 * Validates whether a hidden honeypot field has been filled by an automated bot/scraper.
 */
export function isHoneypotTriggered(honeypotValue?: string | null): boolean {
  if (!honeypotValue) return false;
  return String(honeypotValue).trim().length > 0;
}

// ==========================================
// REAL-TIME SECURITY & THREAT ALERT SYSTEM
// ==========================================

export interface SecurityAlert {
  id?: string;
  type:
    | 'sql_injection_attempt'
    | 'nosql_injection_attempt'
    | 'bot_honeypot'
    | 'brute_force_lockout'
    | 'malformed_payload'
    | 'unauthorized_admin_access'
    | 'document_tampering'
    | 'inactivity_lockout'
    | 'advance_fee_fraud'
    | 'phishing_link';
  severity: 'low' | 'medium' | 'high' | 'critical';
  details: Record<string, any>;
  ip?: string;
  userAgent?: string;
  timestamp?: string;
  createdAt?: any;
  resolved?: boolean;
}

/**
 * Safely logs a security alert or blocked threat event to Firestore /security_alerts.
 * Fails silently so client execution is never interrupted.
 */
export async function logSecurityThreat(
  type: SecurityAlert['type'],
  details: Record<string, any> = {},
  severity: SecurityAlert['severity'] = 'medium'
): Promise<void> {
  try {
    const { collection, addDoc, serverTimestamp } = await import('firebase/firestore');
    const { db } = await import('./firestoreDb');

    const alertData = {
      type,
      severity,
      details,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'server',
      timestamp: new Date().toISOString(),
      createdAt: serverTimestamp(),
      resolved: false,
    };

    await addDoc(collection(db, 'security_alerts'), alertData);
  } catch (err) {
    // Non-blocking security logging fallback
    console.warn('[SECURITY DEFENSE NOTICE] Threat caught and locally recorded:', type, details);
  }
}

/**
 * Fetches recent security alerts for the Admin Platform security dashboard.
 */
export async function getRecentSecurityAlerts(maxLimit = 50): Promise<SecurityAlert[]> {
  try {
    const { collection, getDocs, query, orderBy, limit } = await import('firebase/firestore');
    const { db } = await import('./firestoreDb');

    const alertsCol = collection(db, 'security_alerts');
    const q = query(alertsCol, orderBy('createdAt', 'desc'), limit(maxLimit));
    const snap = await getDocs(q);

    const alerts: SecurityAlert[] = [];
    snap.forEach((docSnap) => {
      alerts.push({ id: docSnap.id, ...(docSnap.data() as any) });
    });
    return alerts;
  } catch (err) {
    console.warn('Could not fetch security alerts:', err);
    return [];
  }
}

/**
 * Subscribes to real-time security alerts for the administrator security dashboard.
 */
export function subscribeToSecurityAlerts(
  callback: (alerts: SecurityAlert[]) => void,
  maxLimit = 50
): () => void {
  let unsubscribe = () => {};

  import('firebase/firestore').then(({ collection, query, orderBy, limit, onSnapshot }) => {
    import('./firestoreDb').then(({ db }) => {
      try {
        const alertsCol = collection(db, 'security_alerts');
        const q = query(alertsCol, orderBy('createdAt', 'desc'), limit(maxLimit));

        unsubscribe = onSnapshot(
          q,
          (snap) => {
            const alerts: SecurityAlert[] = [];
            snap.forEach((docSnap) => {
              alerts.push({ id: docSnap.id, ...(docSnap.data() as any) });
            });
            callback(alerts);
          },
          (err) => {
            console.warn('Security alerts subscription warning:', err);
          }
        );
      } catch (e) {
        console.warn('Failed to attach security alerts listener:', e);
      }
    });
  });

  return () => {
    unsubscribe();
  };
}
